import * as React from "react";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import type {
    ContentIndexSearchResult,
    RecommendedContent,
} from "@volley/data";

export const CARD_WIDTH = 248;

/**
 * Build the in-app route for a recommended content item. Mirrors the navigation
 * used by Content/ContentSearch so a recommendation opens the same place a
 * search hit would.
 */
export function contentNavUrl(content: ContentIndexSearchResult): string {
    if (content.type === "WORKOUT") {
        const metadata = content.metadata as { appId?: number } | null;
        const appId = metadata?.appId ?? 0;
        return `/content/apps/workouts/plugin/play/${appId}/${content.sourceId}`;
    }
    if (content.type === "HELP_ARTICLE") {
        const metadata = content.metadata as { slug?: string } | null;
        return `/help/${metadata?.slug ?? ""}`;
    }
    // JOURNEY (classes)
    return `/content/classes?providerId=${content.contentProvider?.id ?? ""}`;
}

/**
 * Per-type card presentation. Help articles are answers to a support question,
 * not something you "start", so they get their own tag and call to action —
 * without this they render as a green "Workout" card offering to start a
 * workout, which misdescribes what the card actually opens.
 */
const CARD_STYLES = {
    WORKOUT: {
        tagLabel: "Workout",
        tagBg: "rgba(60, 233, 124, 0.92)",
        tagColor: "#0B1730",
        cta: "Start workout",
    },
    JOURNEY: {
        tagLabel: "Class",
        tagBg: "rgba(148, 41, 255, 0.92)",
        tagColor: "#fff",
        cta: "View class",
    },
    HELP_ARTICLE: {
        tagLabel: "Help",
        tagBg: "rgba(90, 162, 255, 0.92)",
        tagColor: "#0B1730",
        cta: "Read article",
    },
} as const;

interface ContentResultCardProps {
    content: RecommendedContent;
    onStart: (content: RecommendedContent) => void;
}

/**
 * A single recommended content card in the Ask Volley result carousel: a tinted
 * hero with a type tag + title, then a meta row, description and a start CTA.
 */
export default function ContentResultCard({
    content,
    onStart,
}: ContentResultCardProps): React.JSX.Element {
    const isClass = content.type === "JOURNEY";
    const isHelp = content.type === "HELP_ARTICLE";
    const { tagLabel, tagBg, tagColor, cta } =
        CARD_STYLES[isHelp ? "HELP_ARTICLE" : isClass ? "JOURNEY" : "WORKOUT"];
    const description = content.overview || content.description || "";
    // A help article is neither sport-specific (they apply to every sport) nor
    // meaningfully "provided by" anyone from a reader's point of view, so the
    // meta row would render as a stray "Volley" with nothing to qualify it.
    const sportLabel = isHelp
        ? null
        : (content.sport?.label ?? content.sport?.name ?? null);
    const providerLabel = isHelp
        ? null
        : (content.contentProvider?.label ??
          content.contentProvider?.name ??
          null);
    const heroImageUrl = content.cardImage?.url;

    return (
        <Box
            sx={{
                flex: `0 0 ${CARD_WIDTH}px`,
                width: CARD_WIDTH,
                scrollSnapAlign: "start",
                bgcolor: "#fff",
                color: "#0B1730",
                borderRadius: 3,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 6px 18px rgba(0, 0, 0, 0.28)",
            }}
        >
            {/* Hero */}
            <Box
                sx={{
                    position: "relative",
                    height: 132,
                    flexShrink: 0,
                    bgcolor: "#0E1B3A",
                    backgroundImage:
                        "radial-gradient(120% 90% at 50% 0%, rgba(60,233,124,0.18) 0%, rgba(60,233,124,0) 60%)",
                    display: "flex",
                    alignItems: "flex-end",
                }}
            >
                {heroImageUrl && (
                    <>
                        {/* Decorative thumbnail; a scrim keeps the title legible. */}
                        <Box
                            component="img"
                            src={heroImageUrl}
                            alt={content.cardImage?.alt ?? ""}
                            loading="lazy"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background:
                                    "linear-gradient(180deg, rgba(11,23,48,0.10) 0%, rgba(11,23,48,0.78) 100%)",
                            }}
                        />
                    </>
                )}
                <Chip
                    label={tagLabel}
                    size="small"
                    sx={{
                        position: "absolute",
                        zIndex: 1,
                        top: 10,
                        left: 10,
                        height: 22,
                        bgcolor: tagBg,
                        color: tagColor,
                        fontWeight: 700,
                        fontSize: 10.5,
                        letterSpacing: "0.02em",
                        textTransform: "uppercase",
                        "& .MuiChip-label": { px: 1 },
                    }}
                />
                <Typography
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        px: 1.75,
                        pb: 1.5,
                        fontWeight: 700,
                        fontSize: 19,
                        lineHeight: 1.12,
                        color: "#fff",
                        letterSpacing: "-0.01em",
                        textShadow: "0 1px 8px rgba(11, 23, 48, 0.55)",
                    }}
                >
                    {content.title}
                </Typography>
            </Box>

            {/* Body */}
            <Stack sx={{ p: 1.75, gap: 1, flex: 1 }}>
                {(sportLabel || providerLabel) && (
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            alignItems: "center",
                            fontSize: 12,
                            color: "#5a6883",
                            fontWeight: 600,
                        }}
                    >
                        {sportLabel && <span>{sportLabel}</span>}
                        {sportLabel && providerLabel && (
                            <Box
                                sx={{
                                    width: 3,
                                    height: 3,
                                    borderRadius: "50%",
                                    bgcolor: "#aab4c8",
                                }}
                            />
                        )}
                        {providerLabel && (
                            <Box
                                component="span"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {providerLabel}
                            </Box>
                        )}
                    </Stack>
                )}
                {description && (
                    <Typography
                        sx={{
                            m: 0,
                            fontSize: 13,
                            lineHeight: 1.45,
                            color: "#46546f",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            minHeight: 56,
                        }}
                    >
                        {description}
                    </Typography>
                )}
                <Button
                    fullWidth
                    onClick={() => onStart(content)}
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 15 }} />}
                    sx={{
                        mt: "auto",
                        borderRadius: 999,
                        textTransform: "none",
                        bgcolor: "#1FA557",
                        color: "#fff",
                        fontWeight: 600,
                        fontSize: 14,
                        py: 1.25,
                        "&:hover": { bgcolor: "#23BC63" },
                    }}
                >
                    {cta}
                </Button>
            </Stack>
        </Box>
    );
}
